import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';

import { getLoads } from 'api/search';
import type { SearchRequestType, SearchResultType } from 'api/search/types';
import { PrettyLoadPreloader } from 'components/PrettyLoadPreloader';
import { useStore } from 'hooks/useStore';
import logger from 'sentry.client.config';
import { SiteFeaturesIDs } from 'store/SiteFeatures/SiteFeatures';
import { LoadType } from 'types/cargosApp/Load';
import { IReactLoadsGridProps } from 'types/global';
import { camelToSnakeObj, cleanEmpty } from 'utils/objects';

type TPrettyLoadProps = Omit<IReactLoadsGridProps, 'siteFeatures' | 'culture'>;

const SIMILAR_POOL_ITEMS_PER_PAGE = 50;
const HIGH_SIMILAR_COUNT = 25;
const BORDERLINE_SIMILAR_COUNT = 11;

function addMockSimilarLoads(loads: LoadType[], pool: LoadType[]): LoadType[] {
  const mainIds = new Set(loads.map(load => load.id));
  const available = pool.filter(load => !mainIds.has(load.id));

  let cursor = 0;
  const pick = (count: number): LoadType[] => {
    if (!available.length) return [];
    const result: LoadType[] = [];
    for (let i = 0; i < count; i += 1) {
      result.push({ ...available[(cursor + i) % available.length] });
    }
    cursor += count;
    return result;
  };

  return loads.map((load, index) => {
    if (index === 0) {
      const similar = pick(HIGH_SIMILAR_COUNT);
      return similar.length ? { ...load, similar } : load;
    }
    if (index === 1) {
      const similar = pick(BORDERLINE_SIMILAR_COUNT);
      return similar.length ? { ...load, similar } : load;
    }

    const hasSimilar = Math.random() > 0.5;
    if (!hasSimilar) return load;

    const similar = pick(Math.floor(Math.random() * 5) + 1);
    return similar.length ? { ...load, similar } : load;
  });
}

const Fallback = () => <div />;
const LoadsGrid = dynamic(
  () =>
    // @ts-ignore
    import('PrettyLoad/PrettyLoad').catch(error => {
      logger.error(error);
      // eslint-disable-next-line no-console
      console.log('error with PrettyLoad loading: ', error);
      return Fallback;
    }),
  {
    loading: () => <PrettyLoadPreloader />,
    ssr: false,
  },
);

const PrettyLoad = (props: TPrettyLoadProps) => {
  const { callbacks, descriptor, currentPage, loads, updateLoad, ...searchResult } = props;

  const {
    app: { locale, loading, serverDate, currentResultFilter },
    siteFeatures: { isSiteFeatureEnabled },
    enablerFeaturesRepo,
    ui: { isCompactPrettyLoad, isPrintMode, setPrintMode, vatRatesDisplay },
    dictionaries: { data },
    boardsRepo: { boardsData },
  } = useStore();

  const isSimilarCargoEnabled = isSiteFeatureEnabled[SiteFeaturesIDs.similarCargoApplications];
  const isVatRatesFeatureEnabled = isSiteFeatureEnabled[SiteFeaturesIDs.newVatRates];

  const [similarPool, setSimilarPool] = useState<LoadType[]>([]);

  const searchKey = loads?.length ? `${currentPage ?? 0}-${loads.length}-${loads[0]?.id ?? ''}` : '';

  useEffect(() => {
    if (!isSimilarCargoEnabled || !searchKey || !currentResultFilter) {
      setSimilarPool([]);
      return;
    }

    let cancelled = false;
    const requestData: SearchRequestType = {
      excludeGeoDicts: true,
      page: (currentPage ?? 1) + 1,
      itemsPerPage: SIMILAR_POOL_ITEMS_PER_PAGE,
      filter: currentResultFilter,
    };

    getLoads(camelToSnakeObj<SearchRequestType>(cleanEmpty(requestData)))
      .then(response => {
        if (cancelled) return;
        const payload = response.data as SearchResultType | undefined;
        setSimilarPool(payload?.loads ?? []);
      })
      .catch(() => {
        if (!cancelled) setSimilarPool([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isSimilarCargoEnabled, searchKey, currentResultFilter, currentPage]);

  const processedLoads = useMemo(() => {
    if (!loads || !isSimilarCargoEnabled) return loads;
    return addMockSimilarLoads(loads, similarPool);
  }, [loads, isSimilarCargoEnabled, similarPool]);

  if (loading.search) {
    return <PrettyLoadPreloader />;
  }

  const shouldSkipRenderPrettyLoad = typeof window === 'undefined' || !loads || !loads.length;
  if (shouldSkipRenderPrettyLoad) return null;

  return (
    <LoadsGrid
      {...searchResult}
      // @ts-ignore
      boards={boardsData}
      callbacks={callbacks}
      culture={locale}
      currentPage={currentPage}
      descriptor={descriptor}
      dictionaries={data}
      enabler={enablerFeaturesRepo}
      isCompactAll={isCompactPrettyLoad}
      isPrintMode={isPrintMode}
      loads={processedLoads}
      selectedVatPercentsForDisplay={
        isVatRatesFeatureEnabled ? vatRatesDisplay.selectedVatPercentsForDisplay : undefined
      }
      serverDate={serverDate}
      setPrintMode={setPrintMode}
      siteFeatures={isSiteFeatureEnabled}
      updateLoad={updateLoad}
      userId={window.atiUser.profile?.ati_code}
    />
  );
};

const connected = observer(PrettyLoad);

export { connected as PrettyLoad };
